import React from 'react';
import {
  Box, Dialog, Drawer, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { plr } from './tokens';

type PlrModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  // id del título, para aria-labelledby.
  titleId: string;
  testId?: string;
  width?: number;
  // El desafío de imágenes del reCAPTCHA se abre fuera del modal y necesita recibir el foco.
  disableEnforceFocus?: boolean;
  // false: sin X y sin cerrar con el fondo o Escape (la persona tiene que elegir algo).
  dismissible?: boolean;
  // Mantiene el contenido montado al cerrar (p. ej. tarjetas de documentos con su estado).
  keepMounted?: boolean;
  // Pie fijo abajo (p. ej. "Continuar") que queda visible aunque el contenido tenga scroll.
  footer?: React.ReactNode;
  children: React.ReactNode;
};

// Modal del rediseño PLR: hoja inferior en mobile y diálogo centrado en desktop, con el título
// y una X para cerrar arriba a la derecha.
export default function PlrModal({
  open, onClose, title, titleId, testId, width, disableEnforceFocus, dismissible = true,
  keepMounted = false, footer, children,
}: PlrModalProps) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('sm'));
  const handleClose = dismissible ? onClose : () => undefined;

  const content = (
    <Box
      data-pw={testId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        fontFamily: plr.font,
        color: plr.ink,
        textAlign: 'left',
      }}
    >
      {!desktop && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            alignSelf: 'center', width: 40, height: 4, borderRadius: '2px', background: plr.heroText,
          }}
        />
      )}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px',
      }}
      >
        <Box
          component="h2"
          id={titleId}
          sx={{
            m: 0, pt: '4px', fontSize: desktop ? 21 : 19, fontWeight: 800,
          }}
        >
          {title}
        </Box>
        {dismissible && (
          <IconButton
            aria-label="Cerrar"
            data-pw={testId ? `${testId}-close` : undefined}
            onClick={onClose}
            sx={{ mr: '-8px', mt: '-4px', color: plr.ink }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      {children}
      {footer && (
        <Box
          data-pw={testId ? `${testId}-footer` : undefined}
          sx={{
            // Pegado al borde inferior de la hoja/diálogo (que es el que hace scroll). El sticky se
            // calcula dentro del padding del papel: bottom y margen negativos lo llevan hasta el
            // borde para que el contenido no asome debajo.
            position: 'sticky',
            bottom: desktop ? '-28px' : '-24px',
            zIndex: 1,
            mx: desktop ? '-28px' : '-24px',
            mb: desktop ? '-28px' : '-24px',
            px: desktop ? '28px' : '24px',
            pt: '12px',
            pb: desktop ? '28px' : '24px',
            mt: '-4px',
            background: '#FFFFFF',
            boxShadow: '0 -10px 16px -10px rgba(26,20,61,0.18)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );

  const backdropSx = { '& .MuiBackdrop-root': { background: plr.overlay } };

  if (desktop) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        disableEnforceFocus={disableEnforceFocus}
        keepMounted={keepMounted}
        aria-labelledby={titleId}
        sx={backdropSx}
        PaperProps={{
          sx: {
            width,
            maxWidth: 'calc(100% - 32px)',
            borderRadius: '24px',
            p: '28px',
            boxSizing: 'border-box',
            boxShadow: '0 16px 40px rgba(26,20,61,0.25)',
          },
        }}
      >
        {content}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      ModalProps={{ disableEnforceFocus, keepMounted }}
      sx={backdropSx}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': titleId,
        sx: {
          borderRadius: '24px 24px 0 0',
          p: '12px 24px 24px',
          boxShadow: '0 -8px 24px rgba(26,20,61,0.18)',
          maxHeight: '90vh',
        },
      } as object}
    >
      {content}
    </Drawer>
  );
}

PlrModal.defaultProps = {
  testId: undefined,
  width: 420,
  disableEnforceFocus: false,
  dismissible: true,
  keepMounted: false,
  footer: undefined,
};
